import { useId } from 'react';
import type {
  CampaignDraft,
  CampaignParticipant,
  RecruitmentOptions,
} from '@rudi/shared/types/recruitment';

export function RecruitmentMultisite({
  draft,
  options,
  onChange,
  editable,
}: {
  draft: CampaignDraft;
  options: RecruitmentOptions;
  onChange: (draft: CampaignDraft) => void;
  editable: boolean;
}) {
  const prefix = useId();
  const participants = draft.participants ?? [];
  const sites = new Map<string, string>();
  for (const study of options.studies)
    for (const site of study.management.recruitment_site_ids)
      sites.set(site, study.centers?.find(center => center.catalog_site_id === site)?.name ?? site);
  for (const participant of participants)
    sites.set(
      participant.siteId,
      participant.siteName ?? sites.get(participant.siteId) ?? participant.siteId
    );
  function change(next: CampaignParticipant[]) {
    onChange({
      ...draft,
      participants: next,
      siteId: next[0]?.siteId ?? '',
      studyIds: [...new Set(next.flatMap(p => p.studyIds))],
    });
  }
  return (
    <div className="min-w-0 space-y-4">
      <p>
        Ivelion bearbeitet diese gemeinsame Kampagne. Teilnehmende Prüfstellen haben Lesezugriff.
      </p>
      {editable && (
        <>
          <div className="space-y-1">
            <label htmlFor={`${prefix}-commissioning`}>Auftraggebende Organisation</label>
            <select
              id={`${prefix}-commissioning`}
              required
              className="block w-full rounded-3xl border bg-background p-2"
              value={draft.commissioningOrganizationId ?? ''}
              onChange={event =>
                onChange({ ...draft, commissioningOrganizationId: event.target.value })
              }
            >
              <option value="">Organisation wählen</option>
              {options.organizations?.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor={`${prefix}-commissioning-kind`}>Auftraggebertyp</label>
            <select
              id={`${prefix}-commissioning-kind`}
              className="block w-full rounded-3xl border bg-background p-2"
              value={draft.commissioningKind ?? 'sponsor'}
              onChange={event =>
                onChange({ ...draft, commissioningKind: event.target.value as 'sponsor' | 'cro' })
              }
            >
              <option value="sponsor">Sponsor</option>
              <option value="cro">CRO</option>
            </select>
          </div>
          <p className="text-sm text-muted-foreground">
            Der Auftrag ändert weder die Zugehörigkeit der Prüfstellen noch die spätere
            Zahlerzuordnung.
          </p>
          <fieldset className="space-y-2">
            <legend>Teilnehmende Prüfstellen</legend>
            {[...sites].map(([id, name]) => (
              <label key={id} className="flex items-start gap-2 break-words rounded-3xl border p-3">
                <input
                  type="checkbox"
                  checked={participants.some(p => p.siteId === id)}
                  onChange={event =>
                    change(
                      event.target.checked
                        ? [...participants, { siteId: id, organizationId: '', studyIds: [] }]
                        : participants.filter(p => p.siteId !== id)
                    )
                  }
                />
                <span className="min-w-0">{name}</span>
              </label>
            ))}
          </fieldset>
        </>
      )}
      {participants.map((participant, index) => (
        <fieldset key={participant.siteId} className="min-w-0 space-y-2 rounded-3xl border p-3">
          <legend className="max-w-full break-words">{sites.get(participant.siteId)}</legend>
          {editable ? (
            <>
              <label htmlFor={`${prefix}-${index}`}>Organisation der Prüfstelle</label>
              <select
                id={`${prefix}-${index}`}
                required
                className="block w-full rounded-3xl border bg-background p-2"
                value={participant.organizationId}
                onChange={event =>
                  change(
                    participants.map(p =>
                      p.siteId === participant.siteId
                        ? { ...p, organizationId: event.target.value }
                        : p
                    )
                  )
                }
              >
                <option value="">Organisation wählen</option>
                {options.organizations?.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              {options.studies
                .filter(study => study.management.recruitment_site_ids.includes(participant.siteId))
                .map(study => (
                  <label key={study.catalog_id} className="flex items-start gap-2 break-words">
                    <input
                      type="checkbox"
                      checked={participant.studyIds.includes(study.catalog_id)}
                      onChange={event =>
                        change(
                          participants.map(p =>
                            p.siteId === participant.siteId
                              ? {
                                  ...p,
                                  studyIds: event.target.checked
                                    ? [...p.studyIds, study.catalog_id]
                                    : p.studyIds.filter(id => id !== study.catalog_id),
                                }
                              : p
                          )
                        )
                      }
                    />
                    <span className="min-w-0">{study.title}</span>
                  </label>
                ))}
            </>
          ) : (
            <>
              <p className="break-words">{participant.organizationName}</p>
              <ul>
                {participant.studyIds.map(id => (
                  <li key={id} className="break-words">
                    {participant.studyTitles?.[id] ?? id}
                  </li>
                ))}
              </ul>
            </>
          )}
        </fieldset>
      ))}
    </div>
  );
}
